import { CacheProvider, EmotionCache } from "@emotion/react"; // 84.1 - 84.1 => 0kb
import { createEmotionCache } from "@/modules/app/emotion/createEmotionCache";
// import {
//   PersistentPlayer,
//   PersistentPlayerState,
// } from "@/modules/persistent-player";
import { appConfig } from "@/config/app-config"; // 0.1 kb
import { WebPageWrapper } from "@/lib/nemmp/web-page-wrapper";
import { createCustomTheme } from "@/modules/web-base";
import CssBaseline from "@mui/material/CssBaseline";
import { Experimental_CssVarsProvider as CssVarsProvider } from "@mui/material/styles"; // 0
import { AppProps } from "next/app";
import Head from "next/head"; // 0.1 kb
import { NextPage } from "next/types";
import "./styles.scss";

import { useEffect } from "react";
import { ntrpc } from "@/utils/trpc";
import { clientContext } from "@/framework/contexts/clientContext";
import { SessionProvider } from "next-auth/react";
import { LegalAcceptanceGate } from "@/modules/legal/LegalAcceptanceGate";
import Box from "@mui/material/Box";

export type NextPageWithLayout<P = {}> = NextPage<P> & {
  getLayout?: (page: React.ReactElement) => React.ReactNode;
  fillViewport?: boolean;
};

const theme = createCustomTheme();

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();
interface MyAppProps extends AppProps {
  // Injected by _document during SSR (a per-request cache); absent in the
  // browser, where the session-wide client cache below is used instead.
  emotionCache?: EmotionCache;
  Component: NextPageWithLayout;
}
const MyApp = (props: MyAppProps) => {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props;
  const getLayout = Component.getLayout || ((page) => <>{page}</>);

  useEffect(() => {
    window.onerror = function (message, file, line, col, error) {
      console.debug(
        "Error caught by window.onerror:",
        message,
        file,
        line,
        col,
        error,
      );
      clientContext.errorReporter.error(error);
      return false;
    };
    window.addEventListener("error", function (e) {
      console.debug("Error caught by window.addEventListener:", e);
      clientContext.errorReporter.error(e.error);
      return false;
    });
    window.addEventListener("unhandledrejection", function (e) {
      console.debug(
        "Error caught by window.addEventListener('unhandledrejection'):",
        e,
      );
      clientContext.errorReporter.error(e.reason);
      return false;
    });
  }, []);

  return (
    <WebPageWrapper appConfig={appConfig}>
      <CacheProvider value={emotionCache}>
        <Head>
          <title>{appConfig.name}</title>
          <meta name="viewport" content="initial-scale=1, width=device-width" />
          <link
            rel="icon"
            type="image/png"
            sizes="128x128"
            href="/assets/favicon.png"
          />
          <link rel="manifest" href="/manifest.json" />
        </Head>
        {/* defaultMode="system" follows the OS appearance; the matching
            CSS-variable scheme is chosen before first paint by the init script
            in _document, so no light-to-dark swap on load.
            See docs/Dark Mode.md. */}
        <CssVarsProvider theme={theme} defaultMode="system">
          <CssBaseline enableColorScheme />
          <SessionProvider session={pageProps.session}>
            <LegalAcceptanceGate>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  ...(Component.fillViewport
                    ? { height: "100vh", overflow: "hidden" }
                    : { minHeight: "100vh" }),
                }}
              >
                {getLayout(
                  <Component
                    // persistentPlayerState={persistentPlayerState}
                    {...pageProps}
                  />,
                )}
              </Box>
            </LegalAcceptanceGate>
          </SessionProvider>

          {/* <PersistentPlayer persistentPlayerState={persistentPlayerState} /> */}
        </CssVarsProvider>
      </CacheProvider>
    </WebPageWrapper>
  );
};

export default ntrpc.withTRPC(MyApp);
